import { useEffect, useState } from "react";
import api from "@/lib/api";
import { ROLE_LABEL } from "@/lib/constants";
import { Search, FileSearch } from "lucide-react";

function fmtDate(iso) {
  if (!iso) return "—";
  try {
    return new Date(iso).toLocaleString(undefined, {
      day: "2-digit", month: "short", year: "numeric",
      hour: "2-digit", minute: "2-digit",
    });
  } catch { return iso; }
}

export default function LeadAccessLog() {
  const [q, setQ] = useState("");
  const [data, setData] = useState({ items: [], total: 0, page: 1, page_size: 50 });
  const [loading, setLoading] = useState(false);

  const fetch = async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/lead-access-log", { params: { q } });
      setData(data);
    } finally { setLoading(false); }
  };
  useEffect(() => { fetch(); /* eslint-disable-next-line */ }, [q]);

  return (
    <div className="p-6 sm:p-8 space-y-6" data-testid="lead-access-log-page">
      <header>
        <p className="text-label">Audit</p>
        <h1 className="text-2xl sm:text-3xl tracking-tight font-semibold mt-1">Lead Access Log</h1>
        <p className="text-sm text-muted-foreground mt-1">
          {data.items.length} of {data.total} entries · who opened which lead, when
        </p>
      </header>

      <div className="relative max-w-md">
        <Search className="absolute left-2.5 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
        <input
          value={q}
          onChange={(e) => setQ(e.target.value)}
          placeholder="Search lead ID, name, company, user…"
          data-testid="access-log-search"
          className="w-full bg-transparent border border-border rounded-sm pl-8 pr-3 py-1.5 text-sm focus:ring-1 focus:ring-[var(--theme-color)] focus:border-[var(--theme-color)] outline-none"
        />
      </div>

      <section className="border border-border rounded-sm bg-background overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead className="bg-secondary/40">
              <tr><Th>Lead ID</Th><Th>Lead Name</Th><Th>Company</Th><Th>Viewed By</Th>
                <Th>Role</Th><Th>Date & Time</Th></tr>
            </thead>
            <tbody>
              {loading && <tr><td colSpan={6} className="text-center py-10 text-muted-foreground">Loading…</td></tr>}
              {!loading && data.items.length === 0 && (
                <tr><td colSpan={6} className="text-center py-12">
                  <FileSearch className="w-8 h-8 text-muted-foreground mx-auto mb-2" />
                  <div className="text-sm text-muted-foreground">No access log entries.</div>
                </td></tr>
              )}
              {data.items.map((row) => (
                <tr key={row.id} className="border-t border-border" data-testid={`access-row-${row.id}`}>
                  <Td><span className="mono text-xs font-medium">{row.display_id}</span></Td>
                  <Td className="font-medium">{row.lead_name}</Td>
                  <Td className="text-muted-foreground">{row.company || "—"}</Td>
                  <Td>{row.viewed_by}</Td>
                  <Td className="text-xs">{ROLE_LABEL[row.user_role] || row.user_role || "—"}</Td>
                  <Td className="text-xs mono text-muted-foreground">{fmtDate(row.opened_at)}</Td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </section>
    </div>
  );
}

function Th({ children }) { return <th className="px-4 py-3 text-left text-[11px] font-semibold uppercase tracking-wider text-muted-foreground">{children}</th>; }
function Td({ children, className = "" }) { return <td className={`px-4 py-3 ${className}`}>{children}</td>; }
